import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBlog } from '../context/BlogContext';
import { motion } from 'framer-motion';
import ConfirmDialog from './ConfirmDialog';

export default function CommentSection({ postId, comments }) {
  const { user } = useAuth();
  const { addComment, deleteComment } = useBlog();
  const [text, setText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await addComment(postId, { author: user.displayName, authorEmail: user.email, authorPhoto: user.picture || '', text: text.trim(), date: new Date().toISOString().slice(0, 10) });
    setText('');
  };

  const handleDeleteConfirm = async () => {
    if (deleteTarget) await deleteComment(postId, deleteTarget);
    setDeleteTarget(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      style={{ marginTop: '3rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '2rem' }}
    >
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete comment"
        message="This comment will be permanently removed."
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem', marginBottom: '1.5rem' }}>
        Comments ({comments.length})
      </h3>

      {comments.map((c) => (
        <div key={c.id} style={{
          background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)',
          padding: '1rem 1.25rem', marginBottom: '0.75rem',
          border: '1px solid var(--border-light)',
        }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {c.authorPhoto ? (
              <img src={c.authorPhoto} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, marginTop: '2px' }} referrerPolicy="no-referrer" />
            ) : (
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, flexShrink: 0, marginTop: '2px', color: 'var(--text-secondary)' }}>{(c.author || 'U')[0]}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.15rem' }}>{c.author}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '0.5rem' }}>{c.date}</div>
                </div>
                {(user?.role === 'admin' || (user && user.email === c.authorEmail)) && (
                  <button
                    onClick={() => setDeleteTarget(c.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-tertiary)', padding: '2px',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent-warm)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    title="Delete comment"
                    aria-label="Delete comment"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                )}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{c.text}</div>
            </div>
          </div>
        </div>
      ))}

      {user ? (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.25rem' }}>
          <label htmlFor="comment-input" style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
            Comment as {user.displayName}
          </label>
          <textarea
            id="comment-input"
            className="form-input form-textarea"
            style={{ minHeight: '80px' }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share your thoughts..."
          />
          <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-end' }}>
            Post Comment
          </button>
        </form>
      ) : (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', marginTop: '1rem' }}>
          Sign in to join the conversation.
        </p>
      )}
    </motion.div>
  );
}